import {
  AuthActions,
  ProfileInfoType,
  Selectors,
  getState,
  widgetDispatch,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {PayloadAction} from '@reduxjs/toolkit';
import {DateTime} from 'luxon';
import {all, call, put, select, takeLatest} from 'redux-saga/effects';

import {BusinessInfo, PersonalInfo} from 'models/Profile';

import {
  EditGuestProfileRequestData,
  INFO_TYPE,
  editGuestProfile,
} from 'services/api/profile/profile';

import {ProfileActions} from './profile.reducer';
import {lastGetProfileFullInfoTimeSelector} from './profile.selectors';
import {getRaaSOrOAuthTokenSaga} from 'utils/getAuthTokenSaga';

export function* updateCompanionSaga(action: PayloadAction<string>) {
  try {
    const primaryaccttoken = yield call(getRaaSOrOAuthTokenSaga);

    if (!primaryaccttoken) {
      return;
    }

    const primaryacctid = yield call(getState, Selectors.userAccountIdSelector);

    const mfatoken = yield call(getState, Selectors.mfaTokenSelector) ?? '';

    const data: EditGuestProfileRequestData = {
      primaryaccttoken,
      mfatoken,
      companion: action.payload,
      responseformat: 'json',
      primaryacctid,
      infotype: INFO_TYPE.COMPANION_NUMBER_OR_PAYMENT_INFO,
    };

    const response = yield call(editGuestProfile, data);

    if (response.data?.guestpreferenceupdate?.resultstatus === 'SUCCESS') {
      yield put(ProfileActions.updateCompanionNumberSuccess(action.payload));
      return;
    }

    if (
      Array.isArray(response.data) &&
      response.data[0]?.['@message'] === 'Invalid Companion Caesars Rewards'
    ) {
      yield put(ProfileActions.updateCompanionNumberInvalid());
      return;
    }

    throw 'Failed to update companion number';
  } catch (err) {
    yield put(ProfileActions.updateCompanionNumberFailure());
  }
}
export function* handleUpdateProfileInfo(
  action: PayloadAction<PersonalInfo | BusinessInfo>
) {
  try {
    const primaryaccttoken = yield call(getRaaSOrOAuthTokenSaga);

    if (!primaryaccttoken) {
      return;
    }

    const primaryacctid = yield call(getState, Selectors.userAccountIdSelector);

    const mfatoken = yield call(getState, Selectors.mfaTokenSelector) ?? '';

    const data: EditGuestProfileRequestData = {
      primaryaccttoken,
      mfatoken,
      responseformat: 'json',
      primaryacctid,
      infotype: INFO_TYPE.PROFILE_INFO,
      ...action.payload,
    };

    const response = yield call(editGuestProfile, data);

    if (response.data?.resultstatus === 'SUCCESS') {
      yield all([
        call(
          widgetDispatch,
          AuthActions.getGuestProfile({
            infoType: ProfileInfoType.fullInfo,
          })
        ),
        put(ProfileActions.updateProfileInfoSuccess()),
      ]);

      return;
    }

    throw new Error(
      response.data?.resultmessage || 'Failed to update profile info'
    );
  } catch (err: any) {
    yield put(ProfileActions.updateProfileInfoFailure());
  }
}

export function* handleGetProfile(
  action: PayloadAction<{infoType: ProfileInfoType}>
) {
  try {
    const {infoType} = action.payload;

    const lastGetProfileFullInfoTime = yield select(
      lastGetProfileFullInfoTimeSelector
    );
    const diffNow = DateTime.fromISO(lastGetProfileFullInfoTime)
      ?.diffNow('minutes')
      .negate();

    if (
      lastGetProfileFullInfoTime &&
      diffNow.minutes < 5 &&
      infoType === ProfileInfoType.fullInfo
    ) {
      return;
    }

    yield call(
      widgetDispatch,
      AuthActions.getGuestProfile({
        infoType,
      })
    );

    const getProfileError = yield call(
      getState,
      Selectors.getProfileFailureSelector
    );

    if (getProfileError) {
      yield put(ProfileActions.clearLastGetProfileFullInfoTime());
      return;
    }

    if (infoType === ProfileInfoType.fullInfo) {
      yield put(ProfileActions.getProfileSuccess());
    }
  } catch (e: any) {}
}

export function* profileSagas() {
  yield all([
    takeLatest(ProfileActions.updateCompanionNumber, updateCompanionSaga),
    takeLatest(ProfileActions.updateProfileInfo, handleUpdateProfileInfo),
    takeLatest(ProfileActions.getProfile, handleGetProfile),
  ]);
}
